import { Badge, Button, Modal, Surface, Table, Td, Textarea, Th, Tr } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SalesOrderStatus } from '@warehouse/shared';
import { ArrowLeft, Ban, Pencil } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { usePermission } from '../../../auth/auth-store';
import { FullPageSpinner } from '../../../components/FullPageSpinner';
import { PageHeader } from '../../../components/PageHeader';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { ORDER_QUERY_KEYS, portalApi, salesOrdersApi } from '../api';
import { DeliveryCalendar } from '../components/DeliveryCalendar';
import { OrderLineEditor, type EditableLine } from '../components/OrderLineEditor';
import { OrderStatusBadge } from '../components/StatusBadge';

/**
 * One order: its lines, its lifecycle actions and its audit history.
 *
 * The status buttons and the edit form are hidden without the permission, but
 * that is cosmetic — the server re-checks every call, and the cutoff bypass on
 * edit is derived from the caller's roles there, never asked for from here.
 */
export function SalesOrderDetailPage(): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = usePermission('orders.outbound.manage');
  const canCancel = usePermission('orders.outbound.cancel');

  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const orderQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.detail(id),
    queryFn: () => salesOrdersApi.detail(id),
    enabled: id.length > 0,
  });
  const historyQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.history(id),
    queryFn: () => salesOrdersApi.history(id),
    enabled: id.length > 0,
  });

  useLiveQuery(['sales-orders'], ORDER_QUERY_KEYS.detail(id), {
    filter: (event) => event.entityId === id,
  });

  const statusMutation = useMutation({
    mutationFn: (status: Exclude<SalesOrderStatus, 'cancelled'>) => salesOrdersApi.setStatus(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => salesOrdersApi.cancel(id, cancelReason.trim() || undefined),
    onSuccess: () => {
      toastSuccess(t('salesOrders:cancel.done'));
      setCancelling(false);
      void queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  if (orderQuery.isLoading) return <FullPageSpinner />;
  const order = orderQuery.data;
  if (!order) return null;

  const editable = order.status === 'ej_hanterad' || order.status === 'pagaende';

  return (
    <>
      <PageHeader
        title={t('salesOrders:detail.title', { orderNo: order.orderNo })}
        subtitle={`${order.restaurantName} · ${order.deliveryDate}`}
        breadcrumb={
          <Link to="/orders/sales" className="inline-flex items-center gap-1 hover:text-ink">
            <ArrowLeft className="size-3" aria-hidden />
            {t('salesOrders:title')}
          </Link>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <OrderStatusBadge status={order.status} />
            {canManage && editable ? (
              <Button
                variant="secondary"
                icon={<Pencil className="size-4" aria-hidden />}
                onClick={() => {
                  setEditing(true);
                }}
              >
                {t('salesOrders:actions.edit')}
              </Button>
            ) : null}
            {canManage
              ? (['pagaende', 'packat', 'skickat'] as Exclude<SalesOrderStatus, 'cancelled'>[])
                  .filter((status) => status !== order.status && order.status !== 'cancelled' && order.status !== 'skickat')
                  .map((status) => (
                    <Button
                      key={status}
                      size="sm"
                      variant="ghost"
                      loading={statusMutation.isPending}
                      onClick={() => {
                        statusMutation.mutate(status);
                      }}
                    >
                      {t(`salesOrders:status.${status}`)}
                    </Button>
                  ))
              : null}
            {canCancel && order.status !== 'cancelled' && order.status !== 'skickat' ? (
              <Button
                variant="ghost"
                icon={<Ban className="size-4" aria-hidden />}
                onClick={() => {
                  setCancelling(true);
                }}
              >
                {t('salesOrders:actions.cancel')}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Surface className="p-5 lg:col-span-2">
          <h2 className="mb-3 font-medium">{t('salesOrders:detail.linesTitle')}</h2>
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>{t('salesOrders:detail.columns.articleNo')}</Th>
                  <Th>{t('salesOrders:detail.columns.article')}</Th>
                  <Th className="text-right">{t('salesOrders:detail.columns.qty')}</Th>
                  <Th>{t('salesOrders:detail.columns.unit')}</Th>
                  <Th className="text-right">{t('salesOrders:detail.columns.qtyBase')}</Th>
                  <Th>{t('salesOrders:detail.columns.status')}</Th>
                </tr>
              </thead>
              <tbody>
                {order.lines.map((line) => (
                  <Tr key={line.id}>
                    <Td className="font-mono text-xs">{line.articleNo}</Td>
                    <Td>{line.articleName}</Td>
                    <Td className="text-right">{line.qty}</Td>
                    <Td>{line.unitCode}</Td>
                    <Td className="text-right">{line.qtyBase}</Td>
                    <Td>
                      <Badge tone="neutral">{t(`salesOrders:detail.lineStatus.${line.status}`)}</Badge>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Surface>

        <div className="flex flex-col gap-4">
          <Surface className="flex flex-col gap-2 p-5 text-sm">
            <Field label={t('salesOrders:list.columns.restaurant')} value={`${order.restaurantName} (${order.restaurantCode})`} />
            <Field label={t('salesOrders:list.columns.deliveryDate')} value={order.deliveryDate} />
            <Field label={t('salesOrders:list.columns.orderDate')} value={order.orderDate} />
            <Field label={t('salesOrders:list.columns.source')} value={t(`salesOrders:source.${order.source}`)} />
            <Field label={t('salesOrders:detail.placedBy')} value={order.placedByName ?? '—'} />
            <Field
              label={t('salesOrders:detail.orderBy')}
              value={
                order.orderByDueAt
                  ? new Date(order.orderByDueAt).toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm' })
                  : t('salesOrders:detail.noCutoff')
              }
            />
            <Field label={t('salesOrders:detail.note')} value={order.note ?? t('salesOrders:detail.noNote')} />
            {order.cutoffBypassed ? (
              <Badge tone="warning">{t('salesOrders:list.cutoffBypassed')}</Badge>
            ) : null}
          </Surface>

          <Surface className="p-5">
            <h2 className="mb-3 font-medium">{t('salesOrders:detail.history')}</h2>
            {(historyQuery.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-ink-muted">{t('salesOrders:detail.historyEmpty')}</p>
            ) : (
              <ul className="flex flex-col gap-2 text-sm" data-testid="order-history">
                {historyQuery.data?.items.map((entry, index) => (
                  <li key={`${entry.at}-${index}`} className="flex flex-col">
                    <span className="font-mono text-xs text-ink-muted">
                      {new Date(entry.at).toLocaleString('sv-SE')}
                    </span>
                    <span>
                      {entry.action}
                      {entry.userName ? ` · ${entry.userName}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Surface>
        </div>
      </div>

      {editing ? (
        <EditOrderModal
          orderId={id}
          restaurantId={order.restaurantId}
          deliveryDate={order.deliveryDate}
          note={order.note ?? ''}
          lines={order.lines.map((line) => ({
            articleId: line.articleId,
            qty: line.qty,
            unitId: line.unitId,
            articleNo: line.articleNo,
            name: line.articleName,
            unitCode: line.unitCode,
          }))}
          onClose={() => {
            setEditing(false);
          }}
          onSaved={() => {
            setEditing(false);
            void queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
          }}
        />
      ) : null}

      {cancelling ? (
        <Modal open onClose={() => { setCancelling(false); }} title={t('salesOrders:cancel.title')} width={480}>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-ink-muted">
              {t('salesOrders:cancel.message', { orderNo: order.orderNo })}
            </p>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">{t('salesOrders:cancel.reason')}</span>
              <Textarea
                rows={2}
                value={cancelReason}
                onChange={(event) => {
                  setCancelReason(event.target.value);
                }}
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setCancelling(false);
                }}
              >
                {t('common:actions.cancel')}
              </Button>
              <Button
                variant="danger"
                loading={cancelMutation.isPending}
                onClick={() => {
                  cancelMutation.mutate(undefined, {
                    onSuccess: () => {
                      void navigate('/orders/sales');
                    },
                  });
                }}
              >
                {t('salesOrders:cancel.confirm')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function Field({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-ink-muted">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------

function EditOrderModal({
  orderId,
  restaurantId,
  deliveryDate: initialDate,
  note: initialNote,
  lines: initialLines,
  onClose,
  onSaved,
}: {
  orderId: string;
  restaurantId: string;
  deliveryDate: string;
  note: string;
  lines: EditableLine[];
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const canBypass = usePermission('orders.outbound.bypassCutoff');
  const [deliveryDate, setDeliveryDate] = useState<string | null>(initialDate);
  const [note, setNote] = useState(initialNote);
  const [lines, setLines] = useState<EditableLine[]>(initialLines);

  const datesQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.deliveryDates(restaurantId, []),
    queryFn: () => portalApi.deliveryDates(restaurantId, []),
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      salesOrdersApi.update(orderId, {
        ...(deliveryDate ? { deliveryDate } : {}),
        note: note.trim() ? note.trim() : null,
        lines: lines.map((l) => ({ articleId: l.articleId, qty: l.qty, ...(l.unitId ? { unitId: l.unitId } : {}) })),
      }),
    onSuccess: () => {
      toastSuccess(t('salesOrders:edit.saved'));
      onSaved();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  // The already-chosen date is not in the selectable set once its cutoff has
  // passed; keep it visible so an admin can save without moving the delivery.
  const dates = datesQuery.data?.dates ?? [];
  const withCurrent = dates.some((d) => d.date === initialDate)
    ? dates
    : [{ date: initialDate, dueAt: null }, ...dates];

  return (
    <Modal open onClose={onClose} title={t('salesOrders:edit.title', { orderNo: '' }).trim()} width={720}>
      <div className="flex flex-col gap-4">
        {canBypass ? (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {t('salesOrders:edit.cutoffBypassNotice')}
          </p>
        ) : null}

        <div>
          <p className="mb-2 text-sm font-medium">{t('salesOrders:create.deliveryDate')}</p>
          <DeliveryCalendar
            dates={withCurrent}
            passed={datesQuery.data?.passed ?? []}
            scheduleMissing={datesQuery.data?.scheduleMissing ?? false}
            loading={datesQuery.isLoading}
            value={deliveryDate}
            onChange={setDeliveryDate}
          />
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">{t('salesOrders:detail.linesTitle')}</p>
          <OrderLineEditor lines={lines} onChange={setLines} />
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('salesOrders:create.note')}</span>
          <Textarea
            rows={2}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </label>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            disabled={lines.length === 0}
            loading={saveMutation.isPending}
            onClick={() => {
              saveMutation.mutate(undefined);
            }}
          >
            {t('salesOrders:actions.saveOrder')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
